import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { Props as BaseEntityLinkProps } from "~/components/base-entity-link";
import { HeaderAllMenuTitle } from "~/components/header/header-oneline-menu-container/header-all-menu/common/header-mega-menu-title";
import { useOnelineMenuTracking } from "~/components/header/header-oneline-menu-container/hooks";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { useEditionValue } from "~/lib/edition";
import type { headerAllMenuColumnistsQuery$key } from "~/queries/__generated__/headerAllMenuColumnistsQuery.graphql";

import {
  AuthorName,
  Container,
  LinkContainer,
  StyledAuthorAvatar,
  StyledEntityLink,
} from "./styles";

export type Props = {
  className?: string;
  queryParameter: BaseEntityLinkProps["query"];
  reference: headerAllMenuColumnistsQuery$key;
};

export const HeaderAllMenuColumnists: FunctionComponent<Props> = ({
  className,
  queryParameter,
  reference: reference_,
}) => {
  const queue = useFragment(
    graphql`
      fragment headerAllMenuColumnistsQuery on Query {
        columnistsQueue: queue(filter: { name: "scmp_columnists" }) {
          items(first: 7) {
            edges {
              node {
                ... on Author {
                  ...entityLink
                  ...authorAvatarAuthor
                  name
                }
              }
            }
          }
        }
      }
    `,
    reference_,
  );

  const { edition: UATrackingEdition } = useOnelineMenuTracking();
  const edition = useEditionValue();

  return (
    <Container className={className}>
      <HeaderAllMenuTitle>SCMP Columnists</HeaderAllMenuTitle>

      <LinkContainer>
        {queue.columnistsQueue?.items?.edges.map((edge, index) => (
          <StyledEntityLink
            key={index}
            query={queryParameter}
            reference={edge.node}
            onClick={() => {
              sendGA4Tracking({
                action: "click",
                category: "menu",
                customized_parameters: {
                  action_type: "redirect",
                  columnist: edge.node.name ?? "",
                  edition,
                  menu_type: "mega",
                },
                subcategory: "section",
              });
              sendGATracking({
                action: "Linemenu/Click",
                category: "Menu",
                label: `${UATrackingEdition}/Linemenu/All/SCMP Columnists/${edge.node.name ?? ""}`,
              });
            }}
          >
            <StyledAuthorAvatar
              height={40}
              reference={edge.node}
              width={40}
              disableLinkingData
              withoutLink
            />
            <AuthorName>{edge.node.name}</AuthorName>
          </StyledEntityLink>
        ))}
      </LinkContainer>
    </Container>
  );
};

HeaderAllMenuColumnists.displayName = "HeaderAllMenuColumnists";
